import { type FC, useMemo } from 'react';
import {
  useDisclosure
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import {
  UiStack,
  UiText,
  UiIconCurrencyCircleDollar,
  uiStyles,
  UiHStack,
  UiTag,
  UiIconProhibit,
  UiIconTicket,
  UiIconPlusCircle,
  UiIconPlus,
  UiIconCheck,
  UiIconCheckCircle,
  UiButton,
  UiIconUsers, UiIconPencilSimple,
} from '@/lib/ui';
import { type TicketData } from '@/api/registration';
import TicketDrawer from './TicketDrawer';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import TimeTag from './TimeTag';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import BaseButtonPlainIcon from '@/base/Button/PlainIcon';
import { useGroupInStockItem } from '@/registration/hook/useGroupInStockItem';
import BaseButtonIconText from '@/base/Button/IconText';
import { CartTempRegistration } from '@/registration/hook/useGroupShoppingCart';
import TicketDrawerOld from '@/registration/register/GroupMainTickets/TicketDrawerOld';
import BaseThemeButton from '@/base/ThemeButton';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(duration);

export interface TicketProps {
  ticket: TicketData
  sharedTicketIds: number[]
  // isAdded?: boolean
  tempRegistrationList?: CartTempRegistration[]
}

const Ticket: FC<TicketProps> = ({
  ticket,
  sharedTicketIds,
  // isAdded = false,
  tempRegistrationList = [],
}) => {
  const { eventId } = useRegisterRoute();
  const { isOpen, onToggle } = useDisclosure();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const groupRegistration = groupRegistrationQuery?.data;

  const displayCapacities = !ticket.hideRemainingStock;
  const displayRemainingTime = !ticket.hideRemainingTime;
  const { isInStock } = useGroupInStockItem(ticket, sharedTicketIds);
  const validToDay = useMemo(() => { return dayjs(ticket.validTo); }
    , [ticket.validTo]);
  const isExpired = useMemo(() => { return validToDay.isBefore(dayjs()); }, [validToDay]);

  const discountStatus = useMemo(() => {
    if (!groupRegistration?.discount || !ticket?.ticketDiscounts) return undefined;
    return (ticket?.ticketDiscounts.find((ticketDiscount) => { return ticketDiscount.active && Number(ticketDiscount.id) === groupRegistration.discount?.id; }))
      ? 'applied' : 'unavailable';
  }, [ticket?.ticketDiscounts, groupRegistration?.discount]);

  const taxStatus = ticket.displayTaxInclusivePrices.isEnabled ? 'inc' : 'ex';
  const taxNames = ticket.displayTaxInclusivePrices.taxName || 'GST';
  const priceLabel = useMemo(
    () => {
      return `${ticket?.priceFormat} (${taxStatus}. ${taxNames})`;
    },
    [ticket, taxStatus, taxNames]
  );
  const discountCode = groupRegistration?.discount?.discountCode;
  const isUnavailable = useMemo(
    () => {
      return isExpired || ticket.currentStock <= 0 || (!isInStock && sharedTicketIds.length > 0);
    },
    [isExpired, isInStock, sharedTicketIds, ticket.currentStock]
  );

  return (
    <>
      <UiStack
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        // onClick={isExpired || ticket.currentStock <= 0 ? undefined : onToggle}
        // enableDivider={true}
        spacing={6}
        opacity={isUnavailable ? 0.6 : 1}
        px={{ base: 6, lg: 8 }}
        py={6}
        shadow={uiStyles.cardShadow}
      >
        {/*<UiStack*/}
        {/*  p={{base: 4, lg: 8}}*/}
        {/*  borderWidth={'1px'}*/}
        {/*  borderColor={'gray.200'}*/}
        {/*  borderStyle={'solid'}*/}
        {/*  // bgColor={'blackAlpha.50'}*/}
        {/*  borderRadius={uiStyles.borderRadius}*/}
        {/*>*/}
          <UiStack spacing={4}>
            <UiStack>
              <UiHStack>
                {isUnavailable ? (
                  <UiIconProhibit />
                ) : (
                  <UiIconTicket />
                )}
                <UiText variant='title'>{ticket?.name}</UiText>
              </UiHStack>
              {!!ticket?.description && (
                <UiText variant='body2' color={'text.secondary'}>{ticket?.description}</UiText>
              )}
            </UiStack>
            <UiHStack>
              {displayRemainingTime && <TimeTag toDate={validToDay} />}
              {ticket.currentStock <= 0 && (<UiTag p={2} colorScheme="gray" size={'sm'}>Out of stock</UiTag>)}
              {discountStatus === 'applied' && <UiTag p={2} colorScheme="green" size={'sm'}>{`${discountCode} applied with ${ticket?.discountValueFormat ?? 0} discount.`}</UiTag>}
              {discountStatus === 'unavailable' && <UiTag p={2} colorScheme="gray" size={'sm'}>Discount unavailable</UiTag>}
            </UiHStack>
          </UiStack>
          <UiHStack justifyContent={'space-between'}>
            <UiHStack>
              <UiIconCurrencyCircleDollar size={'2xl'} color={'text.primary'} />
              <UiText>Price</UiText>
            </UiHStack>
            <UiText>{priceLabel}</UiText>
          </UiHStack>
        {/*</UiStack>*/}
        {!isUnavailable && (
          <>
            <BaseDividerHorizontal height={0} />
            <UiHStack
              justifyContent={'flex-end'}
              // spacing={}
              // onClick={onToggle}
              // bgColor={'red'}
            >
              {/*<BaseButtonPlainIcon*/}
              {/*  label={'Add to cart'}*/}
              {/*  color={'primary.500'}*/}
              {/*  Icon={UiIconPlusCircle}*/}
              {/*  iconSize={'3xl'}*/}
              {/*/>*/}
              {(Array.isArray(tempRegistrationList) && tempRegistrationList.length > 0) ? (
                // <BaseThemeButton
                //   theme={'green'}
                //   onClick={onToggle}
                //   borderWidth={'1px'}
                //   borderStyle={'solid'}
                //   borderColor={'green.300'}
                //   // shadow={uiStyles.cardShadow}
                //   leftIcon={(
                //     <UiHStack spacing={1} pr={2}>
                //       <UiIconUsers size={'xl'} color={'green.600'}/>
                //       <UiText color={'green.600'}>
                //         {`${tempRegistrationList.length}`}
                //       </UiText>
                //     </UiHStack>
                //   )}
                // >
                //   Edit
                // </BaseThemeButton>
                <UiHStack
                  borderRadius={uiStyles.buttonRadius}
                  borderWidth={'1px'}
                  borderColor={'green.500'}
                  borderStyle={'solid'}
                  px={4}
                  py={2}
                  bgColor={'green.500'}
                  spacing={4}
                  onClick={onToggle}
                  {...uiStyles.hover}
                >
                  <UiHStack spacing={1}>
                    <UiIconUsers size={'2xl'} color={'#fff'}/>
                    <UiText color={'#fff'}>
                      {`${tempRegistrationList.length}`}
                    </UiText>
                  </UiHStack>
                  {/*<UiIconPencilSimple color={'#fff'} size={'2xl'}/>*/}
                  <UiText color={'#fff'}>
                    Edit
                  </UiText>
                </UiHStack>
              ) : (
                <UiButton
                  variant={'outline'}
                  onClick={onToggle}
                  leftIcon={<UiIconPlus size={'xl'} color={'primary.500'}/>}
                  px={4}
                  size={'md'}
                >
                  Add
                </UiButton>
              )}
            </UiHStack>
          </>
        )}
      </UiStack>
      <TicketDrawer
        sharedTicketIds={sharedTicketIds}
        ticket={ticket}
        isOpen={isOpen}
        onClose={onToggle}
        displayCapacities={displayCapacities}
      />
    </>
  );
};

export default Ticket;
